import { Box, Flex, Loader, Center } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { EditorHelpers } from 'src/common/components/markdown-editor/plugins/EditorReadyPlugin';
import { GroupedSuggestions } from 'src/common/components/markdown-editor/plugins/SuggestionTrackerPlugin';
import { useAppContainerContext, useContentMeta } from 'src/UI/app-container';
import {
  ChatMode,
  ChatState,
  FinalChunkData,
} from 'src/UI/app-container/chat/hooks/useChatMessages';
import { ContentPaneOverlay } from 'src/UI/app-container/ContentPaneOverlay';
import { SiestaMode } from 'src/UI/app-container/types';
import { useAppContainer } from 'src/UI/app-container/useAppContainer';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { useCreateDocumentVersion } from '../hooks/useCreateDocumentVersion';
import { useDeleteDocumentVersions } from '../hooks/useDeleteDocumentVersions';
import { useDeleteSuitabilityReport } from '../hooks/useDeleteSuitabilityReport';
import { useDocumentVersions } from '../hooks/useDocumentVersions';
import { useRevertToVersion } from '../hooks/useRevertToVersion';
import { useSaveDocument } from '../hooks/useSaveDocument';
import { useSetPrimaryVersion } from '../hooks/useSetPrimaryVersion';
import { DocumentPanel } from './components';
import { EditingOverlay } from './components/EditingOverlay';
import { EditPreviewSheet } from './components/EditPreviewSheet';
import SiestaActionBar from './components/SiestaActionBar';
import { formatVersionDate } from './utils/formatVersionDate';

const EditState = {
  IDLE: 'IDLE',
  EDITING: 'EDITING',
  PREVIEW: 'PREVIEW',
} as const;

type EditState = (typeof EditState)[keyof typeof EditState];

type EditStateData = {
  state: EditState;
  editedContent?: string;
  originalContent?: string;
  assistantMessage?: string;
  versionId?: string | null;
  versionNumber?: number | null;
};

export default function SuitabilityReportEditPage() {
  const { clientId, id } = useParams<{ clientId: string; id: string }>();
  const navigate = useNavigate();
  const {
    deleteSuitabilityReport,
    error: deleteSuitabilityReportError,
    reset: resetDeleteSuitabilityReport,
    isLoading: isRemovingDocument,
  } = useDeleteSuitabilityReport();
  const [suggestions, setSuggestions] = useState<GroupedSuggestions>({
    default: {},
    note: {},
    inline: {},
  });
  const [showMarkers, setShowMarkers] = useState(true);
  const { versions, isLoading: isLoadingVersions, refetch } = useDocumentVersions(id);
  const { createDocumentVersion } = useCreateDocumentVersion(id);
  const { deleteDocumentVersions } = useDeleteDocumentVersions(id);
  const { setPrimaryVersion } = useSetPrimaryVersion(id);
  const { saveDocument } = useSaveDocument(id);
  const { revertToVersion } = useRevertToVersion(id);
  const { createLink } = useFileTemporaryLinkMutation();
  const editorHelpersRef = useRef<EditorHelpers | null>(null);
  const previousSiestaModeRef = useRef<SiestaMode | null>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;
  const { siestaMode, setSiestaMode, setSiestaBusyGlowTop } = useAppContainer();
  const { conversationId } = useAppContainerContext();

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [preInlineEditContent, setPreInlineEditContent] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editState, setEditState] = useState<EditStateData>({ state: EditState.IDLE });

  const inlineSuggestions = useMemo(
    () => Object.values(suggestions.inline || {}),
    [suggestions.inline],
  );

  const primaryVersion = useMemo(() => versions.find(v => v.isPrimary), [versions]);

  const defaultVersion = useMemo(() => {
    if (primaryVersion) return primaryVersion;
    if (versions.length === 0) return undefined;
    return versions.reduce((latest, v) => (v.versionNumber > latest.versionNumber ? v : latest));
  }, [versions, primaryVersion]);

  const selectedVersion = useMemo(() => {
    if (selectedVersionId) {
      const found = versions.find(v => v.id === selectedVersionId);
      if (found) return found;
    }
    return defaultVersion;
  }, [versions, selectedVersionId, defaultVersion]);

  const isViewingPrimary = selectedVersion?.id === primaryVersion?.id;

  useEffect(() => {
    if (inlineSuggestions.length > 0 && actionBarRef.current) {
      const rect = actionBarRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const percent = (centerY / window.innerHeight) * 100;
      setSiestaBusyGlowTop(percent);
    }
  }, [inlineSuggestions.length, setSiestaBusyGlowTop]);

  const getDocumentContent = useCallback(
    () => editorHelpersRef.current?.getContent() ?? selectedVersion?.markdownContent ?? '',
    [selectedVersion?.markdownContent],
  );

  const contentMetadata = useMemo(
    () =>
      selectedVersion?.markdownContent
        ? { documentContent: selectedVersion.markdownContent }
        : undefined,
    [selectedVersion?.markdownContent],
  );

  const handleChatStateChange = useCallback((state: ChatState, data?: FinalChunkData) => {
    if (state === ChatState.EDITING) {
      const currentContent = editorHelpersRef.current?.getContent();
      setEditState(prev => ({
        ...prev,
        state: EditState.EDITING,
        originalContent: currentContent,
      }));
    } else if (state === ChatState.FINAL && data) {
      if (data.versionId !== null && data.versionNumber !== null) {
        setEditState(prev => ({
          state: EditState.PREVIEW,
          editedContent: data.editedContent,
          originalContent: prev.originalContent,
          assistantMessage: data.assistantMessage,
          versionId: data.versionId,
          versionNumber: data.versionNumber,
        }));
        editorHelpersRef.current?.setContent(data.editedContent);
      } else {
        setEditState({ state: EditState.IDLE });
      }
    } else if (state === ChatState.ANSWERING || state === ChatState.THINKING) {
      setEditState(prev => {
        if (prev.state === EditState.EDITING || prev.state === EditState.PREVIEW) {
          return prev;
        }
        return { state: EditState.IDLE };
      });
    }
  }, []);

  useContentMeta({
    pageType: 'suitability-report-edit',
    entityId: id,
    metadata: contentMetadata,
    chatMode: ChatMode.DOCUMENT,
    onChatStateChange: handleChatStateChange,
    getDocumentContent,
  });

  const handleApplyPreview = useCallback(async () => {
    if (editState.versionId) {
      setSelectedVersionId(editState.versionId);
    }
    await refetch();
    setEditState({ state: EditState.IDLE });
  }, [editState.versionId, refetch]);

  const handleDiscardPreview = useCallback(async () => {
    try {
      if (editState.versionId) {
        await deleteDocumentVersions([editState.versionId]);
        await refetch();
      }
    } catch (error) {
      notifyAndLogError('Failed to discard preview', 'Please try again.', error, {
        documentId: id,
        versionId: editState.versionId,
      });
    }
    if (editState.originalContent) {
      editorHelpersRef.current?.setContent(editState.originalContent);
    }
    setEditState({ state: EditState.IDLE });
  }, [editState.originalContent, editState.versionId, deleteDocumentVersions, refetch, id]);

  const handleEditorReady = useCallback((helpers: EditorHelpers) => {
    editorHelpersRef.current = helpers;
  }, []);

  const handleSelectVersion = useCallback(
    async (versionId: string) => {
      try {
        await revertToVersion(versionId);
        setSelectedVersionId(versionId);
      } catch (error) {
        notifyAndLogError('Failed to select version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [revertToVersion, id],
  );

  const handleMakePrimary = useCallback(
    (versionId: string) => {
      setPrimaryVersion(versionId);
      setSelectedVersionId(versionId);
    },
    [setPrimaryVersion],
  );

  const handleSaveVersion = useCallback(async () => {
    try {
      const content = editorHelpersRef.current?.getContent();
      if (!content) return;
      await createDocumentVersion({ markdownContent: content });
      await refetch();
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, { documentId: id });
    }
  }, [createDocumentVersion, refetch, id]);

  const handleRemoveVersion = useCallback(
    async (versionId: string) => {
      try {
        await deleteDocumentVersions([versionId]);
        await refetch();
      } catch (error) {
        notifyAndLogError('Failed to remove version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [deleteDocumentVersions, refetch, id],
  );

  const handleOverwriteVersion = useCallback(
    async (versionId: string) => {
      try {
        const content = editorHelpersRef.current?.getContent();
        if (!content) return;
        await deleteDocumentVersions([versionId]);
        await createDocumentVersion({ markdownContent: content });
        await refetch();
      } catch (error) {
        notifyAndLogError('Failed to overwrite version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [deleteDocumentVersions, createDocumentVersion, refetch, id],
  );

  const handleSave = useCallback(async () => {
    if (!selectedVersion) return;
    setIsSaving(true);
    try {
      const content = editorHelpersRef.current?.getContent();
      if (!content) return;

      const wasPrimary = selectedVersion.isPrimary;
      const result = await createDocumentVersion({ markdownContent: content });
      const newVersionId = result.data.id;

      if (wasPrimary) {
        await setPrimaryVersion(newVersionId);
      }

      setSelectedVersionId(newVersionId);
      await refetch();
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, {
        documentId: id,
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedVersion, createDocumentVersion, setPrimaryVersion, refetch, id]);

  const handleFinalise = useCallback(async () => {
    if (!primaryVersion) return;
    try {
      await saveDocument({ versionId: primaryVersion.id, setAsPrimary: true });
      navigate(`/client/${clientId}/document/suitability-reports`);
    } catch (error) {
      notifyAndLogError('Failed to finalise document', 'Please try again.', error, {
        documentId: id,
        versionId: primaryVersion.id,
      });
    }
  }, [primaryVersion, saveDocument, navigate, clientId, id]);

  const handleDownloadPdf = useCallback(
    async (fileId: number) => {
      try {
        const link = await createLink(fileId);
        window.open(link.url, '_blank');
      } catch (error) {
        notifyAndLogError(
          'Failed to download PDF',
          'An error occurred while trying to download the PDF. Please try again later.',
          error,
          { documentId: id, fileId },
        );
      }
    },
    [createLink, id],
  );

  const handleRemoveDocument = useCallback(async () => {
    if (!clientId || !id) return;
    try {
      await deleteSuitabilityReport(clientId, id);
      navigate(`/client/${clientId}/document/suitability-reports`);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete suitability report',
        'An error occurred while trying to delete the suitability report. Please try again later.',
        e,
        { clientId, reportId: id },
      );
    }
  }, [clientId, id, deleteSuitabilityReport, navigate]);

  const handleInlineEditStart = useCallback(
    (top: number) => {
      const content = editorHelpersRef.current?.getContent();
      if (content) {
        setPreInlineEditContent(content);
      }
      setSiestaBusyGlowTop(top);
      previousSiestaModeRef.current = siestaMode === 'busy' ? 'action' : siestaMode;
      setSiestaMode('busy');
    },
    [siestaMode, setSiestaMode, setSiestaBusyGlowTop],
  );

  const handleApplyInlineChanges = useCallback(() => {
    editorHelpersRef.current?.applyInlineSuggestions();
    setPreInlineEditContent(null);
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');
  }, [setSiestaMode, setSiestaBusyGlowTop]);

  const handleDiscardInlineChanges = useCallback(() => {
    if (preInlineEditContent) {
      editorHelpersRef.current?.setContent(preInlineEditContent);
    }
    setPreInlineEditContent(null);
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');
  }, [preInlineEditContent, setSiestaMode, setSiestaBusyGlowTop]);

  const handleInlineEditComplete = useCallback(() => {
    requestAnimationFrame(() => {
      const currentInlineSuggestions = Object.values(suggestionsRef.current.inline || {});
      if (currentInlineSuggestions.length === 0 && preInlineEditContent !== null) {
        setPreInlineEditContent(null);
        setSiestaBusyGlowTop(null);
        setSiestaMode(previousSiestaModeRef.current ?? 'action');
      }
    });
  }, [preInlineEditContent, setSiestaMode, setSiestaBusyGlowTop]);

  if (isLoadingVersions) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  return (
    <Flex direction="column" h="100%" pos="relative">
      <ContentPaneOverlay>
        <EditPreviewSheet
          opened={editState.state === EditState.PREVIEW}
          editedContent={editState.editedContent ?? ''}
          onApply={handleApplyPreview}
          onDiscard={handleDiscardPreview}
        />
      </ContentPaneOverlay>

      <DocumentPanel
        backHref={`/client/${clientId}/document/suitability-reports`}
        title="Suitability Report"
        version={selectedVersion ? `V${selectedVersion.versionNumber}` : ''}
        versionDate={selectedVersion ? formatVersionDate(selectedVersion.createdAt) : ''}
        versionVariant={isViewingPrimary ? 'primary' : 'past'}
        versions={versions}
        selectedVersionId={selectedVersion?.id}
        suggestions={suggestions}
        showMarkers={showMarkers}
        isSaving={isSaving}
        onShowMarkersChange={setShowMarkers}
        onSave={handleSave}
        onSaveVersion={handleSaveVersion}
        onRemoveVersion={handleRemoveVersion}
        onOverwriteVersion={handleOverwriteVersion}
        onSelectVersion={handleSelectVersion}
        onMakePrimary={handleMakePrimary}
        onDownloadPdf={handleDownloadPdf}
        onFinalise={handleFinalise}
        onRemoveDocument={handleRemoveDocument}
        removeDocumentError={deleteSuitabilityReportError}
        onResetRemoveDocumentError={resetDeleteSuitabilityReport}
        isRemovingDocument={isRemovingDocument}
      />

      <Box mt="md" pos="relative" style={{ flex: 1, minHeight: 0 }}>
        <EditingOverlay mounted={editState.state === EditState.EDITING} />
        <Box h="100%" style={{ overflow: 'auto' }}>
          {selectedVersion?.markdownContent && (
            <MarkdownEditor
              key={selectedVersion.id}
              defaultValue={selectedVersion.markdownContent}
              bare
              onSuggestionsChange={setSuggestions}
              onEditorReady={handleEditorReady}
              showMarkers={showMarkers}
              enableInlineEditing
              documentId={id}
              clientId={clientId}
              conversationId={conversationId}
              onInlineEditStart={handleInlineEditStart}
              onInlineEditComplete={handleInlineEditComplete}
              hasInlineEdits={inlineSuggestions.length > 0}
            />
          )}
        </Box>
      </Box>

      {inlineSuggestions.length > 0 && (
        <Box ref={actionBarRef} pos="absolute" bottom={40} left={32} right={32}>
          <SiestaActionBar
            suggestions={inlineSuggestions}
            onApply={handleApplyInlineChanges}
            onDiscard={handleDiscardInlineChanges}
          />
        </Box>
      )}
    </Flex>
  );
}
